import { withDeadline } from "./deadline.js";
import { readAuthError } from "./auth-error.js";

export type IdentityProvider = "apple" | "google";

export interface ProviderChallenge {
  readonly challenge: string;
  readonly nonce: string;
  readonly expiresIn: number;
}

export interface BrowserSession {
  readonly authenticated?: boolean;
  readonly accountId: string;
  readonly email?: string;
  readonly expiresAt?: string;
}

export interface ProviderAuthorization {
  readonly continueTo: string;
}

/**
 * Turns a refused response into an error the caller can branch on: an
 * `AuthError` carrying the server's code when the server refused the
 * credential, a plain error otherwise.
 */
async function responseError(response: Response, fallback: string): Promise<Error> {
  const refused = await readAuthError(response);
  if (refused) return refused;
  const payload = await response.json().catch(() => undefined) as
    { readonly error?: { readonly message?: string } } | undefined;
  return new Error(payload?.error?.message || fallback);
}

export async function readBrowserSession(apiOrigin: string, fetcher: typeof fetch = fetch): Promise<BrowserSession | null> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/session`, { credentials: "include", signal });
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(`Session check returned ${response.status}`);
    const session = await response.json() as BrowserSession;
    if (!session.accountId) throw new Error("The hosted session did not include an account.");
    return session;
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}

export async function requestProviderChallenge(
  apiOrigin: string,
  purpose: "session" | "link" = "session",
  fetcher: typeof fetch = fetch,
): Promise<ProviderChallenge> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/provider/challenge?client=web&purpose=${purpose}`, {
      method: "POST",
      credentials: "include",
      signal,
    });
    if (!response.ok) throw await responseError(response, "Could not start secure sign-in.");
    const payload = await response.json() as Partial<ProviderChallenge>;
    if (!payload.challenge || !payload.nonce || typeof payload.expiresIn !== "number") {
      throw new Error("The sign-in challenge was incomplete.");
    }
    return payload as ProviderChallenge;
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}

export async function exchangeProviderToken(
  apiOrigin: string,
  provider: IdentityProvider,
  idToken: string,
  challenge: string,
  fetcher: typeof fetch = fetch,
): Promise<BrowserSession> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/provider/session`, {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ provider, idToken, challenge, client: "web" }),
    });
    if (!response.ok) throw await responseError(response, "RecallVault could not finish sign-in.");
    const session = await response.json() as BrowserSession;
    if (!session.accountId) throw new Error("The new session did not include an account.");
    return session;
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}

export async function exchangeProviderAuthorization(
  apiOrigin: string,
  provider: IdentityProvider,
  idToken: string,
  challenge: string,
  fetcher: typeof fetch = fetch,
): Promise<ProviderAuthorization> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/provider/session`, {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ provider, idToken, challenge, client: "web" }),
    });
    if (!response.ok) throw await responseError(response, "RecallVault could not authorize that MCP client.");
    const result = await response.json() as Partial<ProviderAuthorization>;
    const target = new URL(result.continueTo ?? "");
    if (target.origin !== apiOrigin || target.pathname !== "/oauth/consent") {
      throw new Error("The MCP authorization continuation was invalid.");
    }
    return { continueTo: target.toString() };
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}

export async function linkProviderToken(
  apiOrigin: string,
  provider: IdentityProvider,
  idToken: string,
  challenge: string,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/provider/link`, {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ provider, idToken, challenge, client: "web" }),
    });
    if (!response.ok) throw await responseError(response, "RecallVault could not link that identity.");
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}

export async function signOutBrowserSession(apiOrigin: string, fetcher: typeof fetch = fetch): Promise<void> {
  return withDeadline(async (signal) => {
    const response = await fetcher(`${apiOrigin}/auth/logout`, { method: "POST", credentials: "include", signal });
    if (!response.ok && response.status !== 401) throw new Error(`Sign out returned ${response.status}`);
  }, 15_000, "The account service took too long to respond. Try again; your cached vault has not been changed.");
}
